import * as React from "react";
import { Calendar } from "@wellsky/atlas-ui-react";
import type { DateRange } from "@wellsky/atlas-ui-react";
import { CalendarDays, ChevronDown } from "lucide-react";
import { Button, buttonVariants } from "../ui/button";
import { cn } from "../ui/utils";
import { Popover } from "../ui/popover";
import {
  DUE_DATE_PRESETS,
  formatDueDateLabel,
} from "../../utils/dashboardUtils";
import {
  valueToCalendarRange,
  dateRangeToRangeString,
  isDateRangeInvalid,
} from "../../utils/dueDateRange";

interface DateRangeFilterProps {
  /** Current committed dueDate filter value. */
  value: string;
  /** Commit a new dueDate filter value. */
  onChange: (value: string) => void;
  className?: string;
}

/**
 * Top-level "Due date" range control for the MDS/Care tabs (NETSOL-11598).
 * A trigger button opens a popover containing preset shortcuts and an Atlas
 * range calendar. Selection is pending until Apply; Clear resets to the
 * default (overdue + next 7 days). The committed value is a string compatible
 * with FilterState.dueDate (preset label, "start,end" range, or "no_due_date").
 */
export function DateRangeFilter({
  value,
  onChange,
  className,
}: DateRangeFilterProps) {
  const [open, setOpen] = React.useState(false);
  const [pending, setPending] = React.useState(value);

  // Re-sync the pending selection whenever the popover opens or the committed
  // value changes from outside (e.g. tab switch restoring saved filters).
  React.useEffect(() => {
    if (open) setPending(value);
  }, [open, value]);

  const calendarRange = React.useMemo(
    () => valueToCalendarRange(pending),
    [pending],
  );

  // start>end is only reachable for custom ranges; guard Apply.
  const invalid = React.useMemo(
    () => isDateRangeInvalid(calendarRange),
    [calendarRange],
  );

  const handlePreset = (presetValue: string) => setPending(presetValue);

  const handleCalendarChange = (next: DateRange) => {
    setPending(dateRangeToRangeString(next));
  };

  const handleApply = () => {
    onChange(pending);
    setOpen(false);
  };

  // Clear resets to "All due dates" (no date pre-filter), matching the
  // widget's on-load default (NETSOL-11598).
  const handleClear = () => setPending("");

  const trigger = (
    <button
      type="button"
      aria-label={`Due date filter: ${formatDueDateLabel(value)}`}
      className={cn(
        buttonVariants({ variant: "outline", size: "sm" }),
        "h-8 gap-1.5 border-[color:var(--wsc-system-primary)] text-[color:var(--wsc-system-primary)] hover:bg-[var(--wsc-system-surface-container)]",
        className,
      )}
    >
      <CalendarDays className="w-4 h-4 text-[color:var(--wsc-system-primary)]" />
      <span className="text-sm font-semibold">{formatDueDateLabel(value)}</span>
      <ChevronDown className="w-3.5 h-3.5 opacity-70" />
    </button>
  );

  return (
    <Popover
      trigger={trigger}
      open={open}
      onOpenChange={setOpen}
      ariaLabel="Due date range"
      className="p-0"
    >
      <div className="flex bg-[var(--wsc-system-surface)] rounded-md overflow-hidden">
        {/* Preset rail */}
        <div className="flex flex-col min-w-[150px] border-r border-[color:var(--wsc-system-outline)] py-1">
          {DUE_DATE_PRESETS.map((preset) => {
            const active = pending.trim() === preset.value;
            return (
              <button
                key={preset.value}
                type="button"
                onClick={() => handlePreset(preset.value)}
                className={cn(
                  "text-left px-4 py-2 text-sm text-[color:var(--wsc-system-secondary)] hover:bg-[var(--wsc-system-surface-container)]",
                  active &&
                    "bg-[var(--wsc-system-primary-container)] text-[color:var(--wsc-system-primary)] font-semibold border-l-[3px] border-[color:var(--wsc-system-primary)]",
                )}
              >
                {preset.label}
              </button>
            );
          })}
        </div>

        {/* Calendar + footer */}
        <div className="p-3">
          <Calendar
            mode="range"
            monthsToShow={1}
            value={calendarRange}
            onChange={(next) => handleCalendarChange(next as DateRange)}
          />

          {invalid && (
            <p className="mt-2 text-xs text-destructive">
              Start date must be on or before the end date.
            </p>
          )}

          <div className="flex justify-between items-center pt-3 mt-2 border-t border-[color:var(--wsc-system-outline)]">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="text-[color:var(--wsc-system-primary)] uppercase text-xs font-bold"
            >
              Clear
            </Button>
            <Button
              size="sm"
              onClick={handleApply}
              disabled={invalid}
              className="bg-[var(--wsc-system-primary)] hover:bg-[var(--wsc-system-on-primary-fixed-variant)] px-5"
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    </Popover>
  );
}
